/*
 * HONEYWELL INC.,
 * COPYRIGHT (c) 2022
 * ALL RIGHTS RESERVED
 *
 * This software is a copyrighted work and/or information protected as a trade secret.
 * Legal rights of Honeywell Inc. in this software is distinct from ownership of any medium
 * in which the software is embodied. Copyright or trade secret notices included must be
 * reproduced in any copies authorized by Honeywell Inc.
 *
 */

import { useTranslation } from '@scuf/localization';
import React, { memo } from 'react';
import descriptors from '../../../../locales/en-US/descriptors';
import DetailSummaryComponent from '../../../shared/DetailSummaryComponent/DetailSummaryComponent';
import TrendChart from '../../../shared/TrendChart/TrendChart';
import { convertDataForCarbonTrendChart } from '../../../shared/Utils/CarbonViewDetailsUtils';
import { ICarbonAvoidanceData } from '../../../DashboardKPIWidgets/IEnergyManagementProps';
import { KPI_PERCENTAGE_DECIMAL_PRECISION } from '../../../shared/Utils/Constants';
import { getCarbonAvoidanceTotalAggregationValues } from '../../../shared/Utils/unitHandling/EmaasSiteKpiDataScaling';
import {
  getConvertedValue,
  getMaxConvertedValueFromArray
} from '../../../shared/Utils/unitHandling/unitHandling';
import { ConversionType } from '../../../shared/Utils/unitHandling/UnitModel';

interface ICarbonAvoidanceComponentProps {
  data: ICarbonAvoidanceData;
  trendChartCategory: string;
  isCarbonAvoidance: boolean;
}
const CarbonAvoidanceComponent = (props: ICarbonAvoidanceComponentProps) => {
  const d = descriptors.energyManagement;
  const e = descriptors.DetailedView;
  const [t, lng] = useTranslation();
  const { data, trendChartCategory, isCarbonAvoidance } = props;
  const {
    emissionMeasurementUnit,
    totalAggregations,
    monthWiseAggregations
  } = data;

  const {
    totalActualAvoidance,
    totalPreviousPeriodAvoidance,
    totalDeviation,
    totalDeviationPercentage
  } = totalAggregations;

  const valuesArray = getCarbonAvoidanceTotalAggregationValues([
    totalAggregations
  ]);
  const maxValueUnitConversion = getMaxConvertedValueFromArray(
    ConversionType.CARBON,
    emissionMeasurementUnit,
    valuesArray
  );

  const aggregationSummaryData = {
    actualCost: 0,
    baselineCost: 0,
    baselineAvoidanceCost: getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      totalPreviousPeriodAvoidance,
      maxValueUnitConversion.Unit
    ).Value,
    actualAvoidanceCost: getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      totalActualAvoidance,
      maxValueUnitConversion.Unit
    ).Value,
    costSavings: 0,
    costSavingsPercentage: 0,
    avoidanceCostSavings: getConvertedValue(
      ConversionType.CARBON,
      emissionMeasurementUnit,
      totalDeviation,
      maxValueUnitConversion.Unit
    ).Value,
    avoidanceCostSavingsPercentage: parseFloat(
      totalDeviationPercentage.toFixed(KPI_PERCENTAGE_DECIMAL_PRECISION)
    )
  };

  const chartData = convertDataForCarbonTrendChart(
    monthWiseAggregations,
    emissionMeasurementUnit,
    undefined,
    lng
  );

  const legendItems = [
    {
      text: t(d.PREVIOUSAVOIDANCE),
      color: '#4E662B',
      className: 'emaas-legend-item-bar-baseline',
      type: 'Bar'
    },
    {
      text: t(d.ACTUALAVOIDANCE),
      color: '#7DB52C',
      className: 'emaas-legend-item-bar-actual',
      type: 'Bar'
    }
  ];

  return (
    <>
      <DetailSummaryComponent
        aggregationData={aggregationSummaryData}
        measurementUnit={maxValueUnitConversion.Unit}
        detailType={t(e.SCOPEONEKEY)}
        isCarbonAvoidance={true}
      />
      <TrendChart
        measurementUnit={chartData.toUnit}
        chartData={chartData}
        legendItems={legendItems}
        category={trendChartCategory}
        isCarbonAvoidance={isCarbonAvoidance}
      />
    </>
  );
};

export default memo(CarbonAvoidanceComponent);
